import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Clock, MapPin, Navigation, Phone, Send } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "../i18n";
import { CONTACT } from "../data/cars";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactHours = () => {
  const { t, lang } = useLanguage();
  const [form, setForm] = useState({ name: "", phone: "", service: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/leads`, { ...form, lang });
      toast.success(t.contact.sent);
      setForm({ name: "", phone: "", service: "", message: "" });
    } catch {
      toast.error(t.contact.error);
    }
    setSending(false);
  };

  const inputCls =
    "w-full border border-white/15 bg-[#0A0B0E] px-4 py-3 text-sm text-white placeholder-[#6C757D] outline-none transition-colors duration-300 focus:border-[#E63946]";

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative border-t border-white/10 bg-[#0D0F14] py-24 sm:py-32"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
        >
          <p className="font-mono-accent text-xs font-medium uppercase tracking-[0.35em] text-[#E63946]">
            {t.contact.eyebrow}
          </p>
          <h2 className="font-display mt-3 text-3xl font-bold tracking-tight text-white sm:text-4xl">
            {t.contact.title}
          </h2>
        </motion.div>

        <div className="mt-12 grid grid-cols-1 gap-5 lg:grid-cols-2">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7 }}
            className="flex flex-col gap-5"
          >
            <div data-testid="hours-card" className="border border-white/10 bg-[#12141C] p-7">
              <div className="flex items-center gap-3">
                <Clock className="h-5 w-5 text-[#E63946]" />
                <h3 className="font-display text-lg font-bold text-white">
                  {t.contact.hoursTitle}
                </h3>
              </div>
              <div className="mt-5 space-y-3">
                {t.contact.days.map((d, i) => (
                  <div
                    key={i}
                    data-testid={`hours-row-${i}`}
                    className="flex items-center justify-between border-b border-white/5 pb-3 last:border-0"
                  >
                    <span className="text-sm text-[#9E9E9E]">{d.d}</span>
                    <span
                      className={`font-mono-accent text-sm font-bold ${
                        i === 0 ? "text-[#00F5D4]" : "text-[#E63946]"
                      }`}
                    >
                      {d.h}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <a
              href={CONTACT.phoneHref}
              data-testid="contact-phone-card"
              className="group flex items-center justify-between border border-white/10 bg-[#12141C] p-7 transition-colors duration-300 hover:border-[#E63946]/50"
            >
              <div>
                <p className="font-mono-accent text-[11px] uppercase tracking-widest text-[#6C757D]">
                  {t.contact.phoneLabel}
                </p>
                <p className="font-display mt-1 text-2xl font-extrabold text-white group-hover:text-[#E63946]">
                  {CONTACT.phone}
                </p>
              </div>
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#E63946] text-white transition-transform duration-300 group-hover:scale-110">
                <Phone className="h-5 w-5" />
              </span>
            </a>

            <div
              data-testid="contact-address-card"
              className="relative overflow-hidden border border-white/10 bg-[#12141C]"
            >
              <iframe
                title="Family Auto Sales — 8670 Shannon Rd, Shannon, NC 28386"
                src="https://maps.google.com/maps?q=8670%20Shannon%20Rd%2C%20Shannon%2C%20NC%2028386&z=14&output=embed"
                loading="lazy"
                data-testid="map-embed"
                className="h-52 w-full border-0 opacity-90 grayscale-[0.35]"
              />
              <div className="flex flex-wrap items-center justify-between gap-4 p-5">
                <div className="flex items-center gap-3">
                  <span className="relative flex h-10 w-10 items-center justify-center">
                    <span className="pulse-ring absolute inset-0 rounded-full bg-[#E63946]" />
                    <MapPin className="relative h-5 w-5 text-[#E63946]" />
                  </span>
                  <div>
                    <p className="font-mono-accent text-[11px] uppercase tracking-widest text-[#6C757D]">
                      {t.contact.addressLabel}
                    </p>
                    <p className="mt-0.5 text-sm font-semibold text-white">{CONTACT.address}</p>
                  </div>
                </div>
                <a
                  href={CONTACT.mapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="directions-button"
                  className="inline-flex items-center gap-2 rounded-full border border-white/20 px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-white transition-colors duration-300 hover:border-[#E63946] hover:text-[#E63946]"
                >
                  <Navigation className="h-3.5 w-3.5" />
                  {t.contact.directions}
                </a>
              </div>
            </div>
          </motion.div>

          <motion.form
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
            onSubmit={submit}
            data-testid="contact-form"
            className="flex flex-col border border-white/10 bg-[#12141C] p-7"
          >
            <h3 className="font-display text-lg font-bold text-white">{t.contact.formTitle}</h3>
            <div className="mt-6 flex flex-col gap-4">
              <input
                data-testid="contact-form-name"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder={t.contact.name}
                className={inputCls}
              />
              <input
                data-testid="contact-form-phone"
                required
                type="tel"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder={t.contact.phone}
                className={inputCls}
              />
              <select
                data-testid="contact-form-service"
                required
                value={form.service}
                onChange={(e) => setForm({ ...form, service: e.target.value })}
                className={`${inputCls} ${form.service ? "text-white" : "text-[#6C757D]"}`}
              >
                <option value="" disabled>
                  {t.contact.service}
                </option>
                {t.contact.serviceOptions.map((o) => (
                  <option key={o} value={o} className="bg-[#12141C] text-white">
                    {o}
                  </option>
                ))}
              </select>
              <textarea
                data-testid="contact-form-message"
                rows={4}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder={t.contact.message}
                className={`${inputCls} resize-none`}
              />
            </div>
            <button
              type="submit"
              disabled={sending}
              data-testid="contact-form-submit"
              className="mt-6 flex items-center justify-center gap-2 rounded-full bg-[#E63946] px-7 py-3.5 text-sm font-bold uppercase tracking-wider text-white transition-colors duration-300 hover:bg-[#FF4D5A] disabled:opacity-60"
            >
              <Send className="h-4 w-4" />
              {sending ? t.contact.sending : t.contact.send}
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
};

export default ContactHours;
